import axios, { AxiosInstance } from 'axios';
import { Endpoints } from '../endpoints';
import { Document, DocumentObject } from './document';

export interface GetFundingRequests {
  toDate?: string;
  fromDate?: string;
  category?: string;
  status?: string;
}

export interface FundingRequest {
  dateRequested: string;
  category: string;
  status: string;
  claimPaymentId: number;
  claimPaymentItemId: number;
  amountPaid?: string;
}

/**
 * Get funding requests. Results default to the last two years history.
 * @param params
 * @param instance - Axios instance that will be used to query the API.
 * @returns
 */
export const getFundingRequests = async (
  params: GetFundingRequests,
  instance: AxiosInstance = axios,
) => {
  return instance.get<FundingRequest[]>(Endpoints.FundingRequest.LIST, { params });
};

/**
 * Get funding requests. Results default to the last two years history.
 * @param id
 * @param instance - Axios instance that will be used to query the API.
 */
export const getFundingRequest = async (id: number, instance: AxiosInstance = axios) => {
  return instance.get<FundingRequest>(Endpoints.FundingRequest.GET.replace('{id}', `${id}`));
};

/**
 * Get funding requests. Results default to the last two years history.
 * @param id
 * @param instance - Axios instance that will be used to query the API.
 */
export const getFundingRequestDocuments = async (id: number, instance: AxiosInstance = axios) => {
  return instance.get<Document[]>(Endpoints.FundingRequest.GET_DOCUMENTS.replace('{id}', `${id}`));
};

export interface FundingRequestSummaryResponse {
  actionRequired: number;
  issued: number;
  recent: number;
  pending: number;
}

/**
 * Funding request summary totals for the app user.
 * @param instance - Axios instance that will be used to query the API.
 * @returns
 */
export const getFundingRequestSummary = async (instance: AxiosInstance = axios) => {
  return instance.get<FundingRequestSummaryResponse>(Endpoints.FundingRequest.SUMMARY);
};

export interface CreateFundingRequestRequest {
  glCodeId: number;
  fundingRequestNote: string;
  documents: DocumentObject[];
}

/**
 * Create a funding request.
 * Not yet implemented - C2-4502
 * This is currently just a forced OK response.
 * @param data
 * @param instance
 */
export const createFundingRequest = async (
  data: CreateFundingRequestRequest,
  instance: AxiosInstance = axios,
) => instance.post(Endpoints.FundingRequest.CREATE, data);

/**
 * Get a distinct list of funding request statuses.
 * @param instance - Axios instance that will be used to query the API.
 * @returns Array of payment status public descriptions.
 */
export const getStatuses = async (instance: AxiosInstance = axios) => {
  return instance.get(Endpoints.FundingRequest.STATUSES);
};

export interface AddFundingRequestDocumentRequest {
  documents: DocumentObject[];
}

/**
 * Add a document to an existing funding request.
 * @param params
 * @param instance
 */
export const addFundingRequestDocument = async (
  id: number,
  data: AddFundingRequestDocumentRequest,
  instance: AxiosInstance = axios,
) => instance.post(Endpoints.FundingRequest.ADD_DOCUMENT.replace('{id}', `${id}`), data);

export interface FundingRequestGLCodesResponse {
  id: number;
  label: string;
}

export const getFundingRequestGLCodes = async (instance: AxiosInstance = axios) =>
  instance.get<FundingRequestGLCodesResponse[]>(Endpoints.FundingRequest.GL_CODES);
